/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Document Indexing Schema
 * 
 * Types and schemas for indexing documents (PDFs, text files, etc.) for vector search.
 * Supports both local file system and GCS document stores.
 * 
 * Unlike code indexing (which uses AST-aware chunking), document indexing uses
 * semantic chunking based on document structure (headings, paragraphs).
 * 
 * @see product/docs/VECTOR_SEARCH.md - Vector search architecture
 * @see server/services/knowledgeMining/ChunkingService.ts - Chunking strategies
 */

import { z } from 'zod';
import { pgTable, serial, text, integer, timestamp, jsonb, index } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';

// ============================================================================
// PostgreSQL Table Definitions
// ============================================================================

/**
 * Document Stores - tracks indexed document stores (local dirs or GCS buckets)
 */
export const documentStores = pgTable('document_stores', {
  id: serial('id').primaryKey(),
  storeId: text('store_id').notNull().unique(),
  displayName: text('display_name').notNull(),
  sourceType: text('source_type').notNull(), // 'local' or 'gcs'
  sourcePath: text('source_path').notNull(),
  config: jsonb('config').notNull(), // Full store configuration
  status: text('status').notNull().default('not_indexed'), // 'not_indexed', 'indexing', 'indexed', 'index_failed'
  totalDocuments: integer('total_documents').default(0),
  indexedDocuments: integer('indexed_documents').default(0),
  failedDocuments: integer('failed_documents').default(0),
  totalChunks: integer('total_chunks').default(0),
  totalTokens: integer('total_tokens').default(0),
  collectionName: text('collection_name').notNull(), // ChromaDB collection name
  embeddingModel: text('embedding_model').default('text-embedding-3-small'),
  errorMessage: text('error_message'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  lastIndexedAt: timestamp('last_indexed_at'),
}, (table) => ({
  storeIdIdx: index('document_stores_store_id_idx').on(table.storeId),
  statusIdx: index('document_stores_status_idx').on(table.status),
  sourceTypeIdx: index('document_stores_source_type_idx').on(table.sourceType),
}));

/**
 * Indexed Documents - tracks individual documents within a store
 */
export const indexedDocuments = pgTable('indexed_documents', {
  id: serial('id').primaryKey(),
  storeId: text('store_id').notNull().references(() => documentStores.storeId, { onDelete: 'cascade' }),
  documentId: text('document_id').notNull(),
  name: text('name').notNull(),
  sourcePath: text('source_path').notNull(),
  format: text('format'), // 'pdf', 'txt', 'md', 'docx', 'pptx', 'html'
  fileSizeBytes: integer('file_size_bytes'),
  chunkCount: integer('chunk_count').default(0),
  totalTokens: integer('total_tokens').default(0),
  status: text('status').notNull().default('not_indexed'), // 'not_indexed', 'indexing', 'indexed', 'index_failed'
  errorMessage: text('error_message'),
  metadata: jsonb('metadata'), // Extracted document metadata
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
  lastIndexedAt: timestamp('last_indexed_at'),
}, (table) => ({
  storeIdIdx: index('indexed_documents_store_id_idx').on(table.storeId),
  documentIdIdx: index('indexed_documents_document_id_idx').on(table.documentId),
  statusIdx: index('indexed_documents_status_idx').on(table.status),
  storeDocIdx: index('indexed_documents_store_doc_idx').on(table.storeId, table.documentId),
}));

// Drizzle insert/select schemas
export const insertDocumentStoreSchema = createInsertSchema(documentStores).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const selectDocumentStoreSchema = createSelectSchema(documentStores);

export const insertIndexedDocumentSchema = createInsertSchema(indexedDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const selectIndexedDocumentSchema = createSelectSchema(indexedDocuments);

// Type exports from Drizzle tables
export type DocumentStoreRow = typeof documentStores.$inferSelect;
export type InsertDocumentStoreRow = typeof documentStores.$inferInsert;
export type IndexedDocumentRow = typeof indexedDocuments.$inferSelect;
export type InsertIndexedDocumentRow = typeof indexedDocuments.$inferInsert;

// ============================================================================
// Document Source Types
// ============================================================================

/**
 * Supported document source types
 */
export const DocumentSourceType = z.enum([
  'local',    // Local file system path
  'gcs',      // Google Cloud Storage
]);
export type DocumentSourceType = z.infer<typeof DocumentSourceType>;

/**
 * Supported document formats for extraction
 */
export const SupportedDocumentFormat = z.enum([
  'pdf',
  'txt',
  'md',
  'docx',
  'pptx',
  'html',
]);
export type SupportedDocumentFormat = z.infer<typeof SupportedDocumentFormat>;

// ============================================================================
// Document Store Configuration
// ============================================================================

/**
 * Local document store configuration
 */
export const LocalDocumentStoreConfig = z.object({
  type: z.literal('local'),
  /** Absolute path to the document directory or file */
  path: z.string().min(1),
  /** Human-readable display name */
  displayName: z.string().min(1),
  /** File patterns to include (glob patterns) */
  includePatterns: z.array(z.string()).optional(),
  /** File patterns to exclude (glob patterns) */
  excludePatterns: z.array(z.string()).optional(),
});
export type LocalDocumentStoreConfig = z.infer<typeof LocalDocumentStoreConfig>;

/**
 * GCS document store configuration
 */
export const GcsDocumentStoreConfig = z.object({
  type: z.literal('gcs'),
  /** GCS bucket name */
  bucketName: z.string().min(1),
  /** Path prefix within the bucket (optional) */
  prefix: z.string().optional(),
  /** Human-readable display name */
  displayName: z.string().min(1),
  /** File patterns to include (glob patterns) */
  includePatterns: z.array(z.string()).optional(),
  /** File patterns to exclude (glob patterns) */
  excludePatterns: z.array(z.string()).optional(),
});
export type GcsDocumentStoreConfig = z.infer<typeof GcsDocumentStoreConfig>;

/**
 * Union type for document store configuration
 */
export const DocumentStoreConfig = z.discriminatedUnion('type', [
  LocalDocumentStoreConfig,
  GcsDocumentStoreConfig,
]);
export type DocumentStoreConfig = z.infer<typeof DocumentStoreConfig>;

// ============================================================================
// Document Indexing Options
// ============================================================================

/**
 * Chunking strategy for documents
 */
export const DocumentChunkingStrategy = z.enum([
  'fixed',     // Fixed-size chunks
  'semantic',  // Section-aware chunking (heuristic)
  'hybrid',    // Semantic with fixed fallback
  'rlm',       // RLM-based semantic chunking (LLM-guided)
]);
export type DocumentChunkingStrategy = z.infer<typeof DocumentChunkingStrategy>;

/**
 * Document indexing options
 */
export const DocumentIndexingOptions = z.object({
  /** Chunking strategy */
  chunkingStrategy: DocumentChunkingStrategy.default('hybrid'),
  /** Target tokens per chunk */
  targetTokensPerChunk: z.number().min(100).max(4000).default(1000),
  /** Maximum tokens per chunk */
  maxTokensPerChunk: z.number().min(200).max(8000).default(2000),
  /** Overlap tokens between chunks */
  overlapTokens: z.number().min(0).max(500).default(100),
  /** Force re-indexing even if already indexed */
  force: z.boolean().default(false),
  /** Extract document metadata (title, author, etc.) */
  extractMetadata: z.boolean().default(true),
  /** Model to use for RLM chunking (only used when chunkingStrategy is 'rlm') */
  rlmModel: z.string().optional(),
});
export type DocumentIndexingOptions = z.infer<typeof DocumentIndexingOptions>;

/**
 * Default indexing options
 */
export const DEFAULT_DOCUMENT_INDEXING_OPTIONS: DocumentIndexingOptions = {
  chunkingStrategy: 'hybrid',
  targetTokensPerChunk: 1000,
  maxTokensPerChunk: 2000,
  overlapTokens: 100,
  force: false,
  extractMetadata: true,
  rlmModel: undefined,
};

// ============================================================================
// Document Indexing Status
// ============================================================================

/**
 * Status of a document in the index
 */
export const DocumentIndexStatus = z.enum([
  'not_indexed',   // Document not indexed
  'indexing',      // Currently indexing
  'indexed',       // Successfully indexed
  'index_failed',  // Indexing failed
]);
export type DocumentIndexStatus = z.infer<typeof DocumentIndexStatus>;

/**
 * Indexed document information
 */
export const IndexedDocumentInfo = z.object({
  /** Unique document ID */
  documentId: z.string(),
  /** Document name/title */
  name: z.string(),
  /** Source type */
  sourceType: DocumentSourceType,
  /** Source path or URL */
  sourcePath: z.string(),
  /** Index status */
  status: DocumentIndexStatus,
  /** Number of chunks created */
  chunkCount: z.number().optional(),
  /** Total tokens in the document */
  totalTokens: z.number().optional(),
  /** Document format */
  format: SupportedDocumentFormat.optional(),
  /** File size in bytes */
  fileSizeBytes: z.number().optional(),
  /** Indexing progress (0-100) */
  progress: z.number().optional(),
  /** Error message if failed */
  errorMessage: z.string().optional(),
  /** When the document was last indexed */
  lastIndexedAt: z.string().optional(),
  /** Extracted metadata */
  metadata: z.record(z.any()).optional(),
});
export type IndexedDocumentInfo = z.infer<typeof IndexedDocumentInfo>;

/**
 * Document store status
 */
export const DocumentStoreStatus = z.object({
  /** Store ID */
  storeId: z.string(),
  /** Store display name */
  displayName: z.string(),
  /** Source type */
  sourceType: DocumentSourceType,
  /** Overall status */
  status: DocumentIndexStatus,
  /** Total documents in store */
  totalDocuments: z.number(),
  /** Documents successfully indexed */
  indexedDocuments: z.number(),
  /** Documents with errors */
  failedDocuments: z.number(),
  /** Total chunks across all documents */
  totalChunks: z.number(),
  /** Total tokens across all documents */
  totalTokens: z.number(),
  /** Indexing progress (0-100) */
  progress: z.number().optional(),
  /** Last indexed timestamp */
  lastIndexedAt: z.string().optional(),
  /** Individual document info */
  documents: z.array(IndexedDocumentInfo).optional(),
});
export type DocumentStoreStatus = z.infer<typeof DocumentStoreStatus>;

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Request to index a document store
 */
export const IndexDocumentStoreRequest = z.object({
  /** Document store configuration */
  store: DocumentStoreConfig,
  /** Indexing options */
  options: DocumentIndexingOptions.optional(),
});
export type IndexDocumentStoreRequest = z.infer<typeof IndexDocumentStoreRequest>;

/**
 * Request to index a single document
 */
export const IndexSingleDocumentRequest = z.object({
  /** Source type */
  sourceType: DocumentSourceType,
  /** Path (local) or GCS URL (gs://bucket/path) */
  path: z.string().min(1),
  /** Display name */
  displayName: z.string().min(1),
  /** Indexing options */
  options: DocumentIndexingOptions.optional(),
});
export type IndexSingleDocumentRequest = z.infer<typeof IndexSingleDocumentRequest>;

/**
 * Response from indexing operation
 */
export const IndexDocumentResponse = z.object({
  success: z.boolean(),
  /** Store/document ID for tracking */
  storeId: z.string(),
  /** Message */
  message: z.string(),
  /** Whether indexing is running in background */
  isBackgroundJob: z.boolean().default(false),
  /** Initial status */
  status: DocumentStoreStatus.optional(),
  /** Error details if failed */
  error: z.string().optional(),
});
export type IndexDocumentResponse = z.infer<typeof IndexDocumentResponse>;

/**
 * Request to search indexed documents
 */
export const SearchDocumentsRequest = z.object({
  /** Search query */
  query: z.string().min(1),
  /** Limit to specific store ID */
  storeId: z.string().optional(),
  /** Maximum results */
  limit: z.number().min(1).max(100).default(10),
  /** Minimum relevance score (0-1) */
  minScore: z.number().min(0).max(1).optional(),
});
export type SearchDocumentsRequest = z.infer<typeof SearchDocumentsRequest>;

/**
 * Search result item
 */
export const DocumentSearchResult = z.object({
  /** Document ID */
  documentId: z.string(),
  /** Document name */
  documentName: z.string(),
  /** Chunk content */
  content: z.string(),
  /** Relevance score */
  score: z.number(),
  /** Chunk index */
  chunkIndex: z.number(),
  /** Total chunks in document */
  totalChunks: z.number(),
  /** Section path (if available) */
  sectionPath: z.array(z.string()).optional(),
  /** Source path */
  sourcePath: z.string(),
  /** Source type */
  sourceType: DocumentSourceType,
});
export type DocumentSearchResult = z.infer<typeof DocumentSearchResult>;

/**
 * Search response
 */
export const SearchDocumentsResponse = z.object({
  success: z.boolean(),
  /** Search results */
  results: z.array(DocumentSearchResult),
  /** Total results found */
  totalResults: z.number(),
  /** Query used */
  query: z.string(),
  /** Time taken in ms */
  searchTimeMs: z.number().optional(),
});
export type SearchDocumentsResponse = z.infer<typeof SearchDocumentsResponse>;

// ============================================================================
// Database Types (for persistence)
// ============================================================================

/**
 * Document store database record
 * Stores metadata about indexed document stores
 */
export const DocumentStore = z.object({
  /** Database ID */
  id: z.number(),
  /** Store ID (used as ChromaDB collection reference) */
  storeId: z.string(),
  /** Display name */
  displayName: z.string(),
  /** Source type: 'local' or 'gcs' */
  sourceType: DocumentSourceType,
  /** Source path (local path or GCS bucket/prefix) */
  sourcePath: z.string(),
  /** Full store configuration as JSON */
  config: z.any(), // DocumentStoreConfig
  /** Indexing status */
  status: DocumentIndexStatus,
  /** Total documents in store */
  totalDocuments: z.number().default(0),
  /** Documents indexed */
  indexedDocuments: z.number().default(0),
  /** Documents with errors */
  failedDocuments: z.number().default(0),
  /** Total chunks */
  totalChunks: z.number().default(0),
  /** Total tokens */
  totalTokens: z.number().default(0),
  /** ChromaDB collection name */
  collectionName: z.string(),
  /** Embedding model used */
  embeddingModel: z.string().default('text-embedding-3-small'),
  /** Error message if status is index_failed */
  errorMessage: z.string().nullable().optional(),
  /** Created timestamp */
  createdAt: z.date().nullable().optional(),
  /** Updated timestamp */
  updatedAt: z.date().nullable().optional(),
  /** Last successful index timestamp */
  lastIndexedAt: z.date().nullable().optional(),
});
export type DocumentStore = z.infer<typeof DocumentStore>;

/**
 * Insert document store type (without auto-generated fields)
 */
export const InsertDocumentStore = DocumentStore.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertDocumentStore = z.infer<typeof InsertDocumentStore>;

/**
 * Document record within a store
 * Tracks individual documents and their indexing status
 */
export const IndexedDocument = z.object({
  /** Database ID */
  id: z.number(),
  /** Parent store ID */
  storeId: z.string(),
  /** Document ID */
  documentId: z.string(),
  /** Document name */
  name: z.string(),
  /** Source path */
  sourcePath: z.string(),
  /** Document format */
  format: SupportedDocumentFormat.nullable().optional(),
  /** File size in bytes */
  fileSizeBytes: z.number().nullable().optional(),
  /** Number of chunks */
  chunkCount: z.number().default(0),
  /** Total tokens */
  totalTokens: z.number().default(0),
  /** Index status */
  status: DocumentIndexStatus,
  /** Error message if failed */
  errorMessage: z.string().nullable().optional(),
  /** Extracted metadata as JSON */
  metadata: z.any().nullable().optional(),
  /** Created timestamp */
  createdAt: z.date().nullable().optional(),
  /** Updated timestamp */
  updatedAt: z.date().nullable().optional(),
  /** Last indexed timestamp */
  lastIndexedAt: z.date().nullable().optional(),
});
export type IndexedDocument = z.infer<typeof IndexedDocument>;

/**
 * Insert indexed document type
 */
export const InsertIndexedDocument = IndexedDocument.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertIndexedDocument = z.infer<typeof InsertIndexedDocument>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get document format from file extension
 */
export function getDocumentFormatFromPath(filePath: string): SupportedDocumentFormat | null {
  const ext = filePath.toLowerCase().split('.').pop();
  switch (ext) {
    case 'pdf': return 'pdf';
    case 'txt': return 'txt';
    case 'md': case 'markdown': return 'md';
    case 'docx': return 'docx';
    case 'pptx': return 'pptx';
    case 'html': case 'htm': return 'html';
    default: return null;
  }
}

/**
 * Check if a file format is supported
 */
export function isSupportedDocumentFormat(filePath: string): boolean {
  return getDocumentFormatFromPath(filePath) !== null;
}

/**
 * Generate a store ID from configuration
 */
export function generateStoreId(config: DocumentStoreConfig): string {
  if (config.type === 'local') {
    // Use path hash for local stores
    const pathParts = config.path.split('/').filter(Boolean);
    return `local-${pathParts.slice(-2).join('-')}`.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  } else {
    // Use bucket + prefix for GCS stores
    const prefix = config.prefix ? `-${config.prefix.replace(/\//g, '-')}` : '';
    return `gcs-${config.bucketName}${prefix}`.toLowerCase().replace(/[^a-z0-9-]/g, '-');
  }
}

/**
 * Generate a document ID from source
 */
export function generateDocumentId(sourceType: DocumentSourceType, sourcePath: string): string {
  const pathParts = sourcePath.split('/').filter(Boolean);
  const name = pathParts.pop() || 'unknown';
  const parent = pathParts.pop() || '';
  const prefix = sourceType === 'gcs' ? 'gcs' : 'local';
  return `${prefix}-${parent}-${name}`.toLowerCase().replace(/[^a-z0-9-]/g, '-');
}

/**
 * Estimate tokens from character count (approximate)
 * Named differently to avoid collision with playbooks.estimateTokens
 */
export function estimateDocumentTokens(text: string): number {
  // Approximate: 1 token ≈ 4 characters for English text
  return Math.ceil(text.length / 4);
}
